import actionCreator from '../../shared/decorators/actionCreator';
import recordForUndo from '../../shared/decorators/recordForUndo';
import {
  BACKGROUND_UPDATE_RECORDING_GROUP,
  REDUX_NAMESPACE,
} from '../constants';
import {
  POSITION_UPDATE_RECORDING_GROUP,
  SCALE_UPDATE_RECORDING_GROUP,
} from '../../../constants';
import { DEFAULT_KEYFRAME_NUMBER } from '../../creative/constants';
import { PASTE_SHOT_LAYOUT_INFO_FROM_CLIPBOARD } from '../../clipboard/constants';
import enqueueGAEvent from '../../shared/decorators/enqueueGAEvent';
import { EVENT_CATEGORY } from '../../analytics/constants';

const ACTION_CREATORS = {
  @recordForUndo()
  @actionCreator(REDUX_NAMESPACE)
  addChapterBreak(chapterBreakFrameStart) {
    return {
      chapterBreakFrameStart,
    };
  },

  @enqueueGAEvent(
    EVENT_CATEGORY.CREATIVE_UPDATE.NAME,
    EVENT_CATEGORY.CREATIVE_UPDATE.ACTION.APPEND_CLIP,
  )
  @recordForUndo()
  @actionCreator(REDUX_NAMESPACE)
  appendVideoFile(file) {
    return {
      file,
    };
  },

  @actionCreator(REDUX_NAMESPACE)
  chooseVideoFile(file) {
    return {
      file,
    };
  },

  @enqueueGAEvent(
    EVENT_CATEGORY.CREATIVE_UPDATE.NAME,
    EVENT_CATEGORY.CREATIVE_UPDATE.ACTION.CREATE_CLIP_FROM_SHOTS,
  )
  @recordForUndo()
  @actionCreator(REDUX_NAMESPACE)
  createClipFromSelectedShots() {},

  @recordForUndo()
  @actionCreator(REDUX_NAMESPACE)
  deleteChapterBreak(chapterBreakFrameStart) {
    return {
      chapterBreakFrameStart,
    };
  },

  @actionCreator(REDUX_NAMESPACE)
  deleteChapterBreaks(chapterBreakFrameStarts) {
    return {
      chapterBreakFrameStarts,
    };
  },

  @recordForUndo()
  @actionCreator(REDUX_NAMESPACE)
  deleteVideoClip(videoClip) {
    return {
      videoClip,
    };
  },

  @recordForUndo(PASTE_SHOT_LAYOUT_INFO_FROM_CLIPBOARD)
  @actionCreator(REDUX_NAMESPACE)
  applyCopiedShotLayoutProperties() {},

  @actionCreator(REDUX_NAMESPACE)
  resetChapterBreaks() {},

  @actionCreator(REDUX_NAMESPACE)
  endShotOperation() {},

  @recordForUndo()
  @actionCreator(REDUX_NAMESPACE)
  mergeShots() {},

  @recordForUndo()
  @actionCreator(REDUX_NAMESPACE)
  moveVideoClip(videoClip, indexDelta) {
    return {
      indexDelta,
      videoClip,
    };
  },

  @actionCreator(REDUX_NAMESPACE)
  scrubTimeline(frame) {
    return {
      frame,
    };
  },

  @actionCreator(REDUX_NAMESPACE)
  pollForShotList() {},

  @actionCreator(REDUX_NAMESPACE)
  selectShot(frame) {
    return {
      frame,
    };
  },

  @actionCreator(REDUX_NAMESPACE)
  reselectShotRange(frame, indexIntoShotRanges, isForBottom) {
    return {
      frame,
      indexIntoShotRanges,
      isForBottom,
    };
  },

  @actionCreator(REDUX_NAMESPACE)
  selectPreviousShot() {
    return {};
  },

  @actionCreator(REDUX_NAMESPACE)
  selectNextShot() {
    return {};
  },

  @actionCreator(REDUX_NAMESPACE)
  selectAllShots() {
    return {};
  },

  @actionCreator(REDUX_NAMESPACE)
  selectPreviousClip() {
    return {};
  },

  @actionCreator(REDUX_NAMESPACE)
  selectNextClip() {
    return {};
  },

  /**
   * Select Shot Background - (blur or fill)
   * @param {ShotBackground} shotBackground
   * @returns {{shotBackground: ShotBackground}}
   */
  @recordForUndo(BACKGROUND_UPDATE_RECORDING_GROUP)
  @actionCreator(REDUX_NAMESPACE)
  selectShotBackground(shotBackground) {
    return {
      shotBackground,
    };
  },

  @recordForUndo()
  @actionCreator(REDUX_NAMESPACE)
  selectShotLayout(shotLayoutType, shotLayoutSize, shotLayoutViewHeights) {
    return {
      shotLayoutSize,
      shotLayoutType,
      shotLayoutViewHeights,
    };
  },

  @actionCreator(REDUX_NAMESPACE)
  selectShotRange(selectedShotRange) {
    return {
      selectedShotRange,
    };
  },

  @actionCreator(REDUX_NAMESPACE)
  selectShotRanges(selectedShotRanges) {
    return {
      selectedShotRanges,
    };
  },

  @actionCreator(REDUX_NAMESPACE)
  setAllShotsDetected(allShotsDetected) {
    return {
      allShotsDetected,
    };
  },

  @actionCreator(REDUX_NAMESPACE)
  setIsCreatingEpisode(isCreatingEpisode) {
    return {
      isCreatingEpisode,
    };
  },

  @actionCreator(REDUX_NAMESPACE)
  setIsEpisodeTimelineScrolling(isEpisodeTimelineScrolling) {
    return {
      isEpisodeTimelineScrolling,
    };
  },

  @actionCreator(REDUX_NAMESPACE)
  setIsLoadingBreaks(isLoadingBreaks) {
    return {
      isLoadingBreaks,
    };
  },

  @actionCreator(REDUX_NAMESPACE)
  setIsShotBreaksShown(isShotBreaksShown) {
    return {
      isShotBreaksShown,
    };
  },

  @actionCreator(REDUX_NAMESPACE)
  setOriginalShotsInformation(shots, shotsByFrameRange) {
    return {
      shots,
      shotsByFrameRange,
    };
  },

  @actionCreator(REDUX_NAMESPACE)
  setShotListComplete(shotListComplete) {
    return {
      shotListComplete,
    };
  },

  @recordForUndo()
  @actionCreator(REDUX_NAMESPACE)
  setShotTransformationType(elementId, shotTransformationType) {
    return {
      elementId,
      shotTransformationType,
    };
  },

  @actionCreator(REDUX_NAMESPACE)
  setShotsProcessed() {},

  @recordForUndo()
  @actionCreator(REDUX_NAMESPACE)
  setZoom(isZoomedIn) {
    return {
      isZoomedIn,
    };
  },

  @actionCreator(REDUX_NAMESPACE)
  startShotOperation() {},

  @recordForUndo()
  @actionCreator(REDUX_NAMESPACE)
  splitShotElement() {},

  @recordForUndo()
  @actionCreator(REDUX_NAMESPACE)
  toggleSkipForSelectedShots() {},

  @recordForUndo()
  @actionCreator(REDUX_NAMESPACE)
  toggleZoom() {},

  @recordForUndo()
  @actionCreator(REDUX_NAMESPACE)
  updateChaptersByChapterBreak(currentChapterBreakFrame, newChapterBreakFrame) {
    return {
      currentChapterBreakFrame,
      newChapterBreakFrame,
    };
  },

  @recordForUndo(POSITION_UPDATE_RECORDING_GROUP)
  @actionCreator(REDUX_NAMESPACE)
  setPositionOfMultipleShots(
    xValues,
    yValues,
    transformElementIds,
    currentFrame = DEFAULT_KEYFRAME_NUMBER,
    isUserTransform = true,
  ) {
    return {
      currentFrame,
      isUserTransform,
      transformElementIds,
      xValues,
      yValues,
    };
  },

  @recordForUndo(SCALE_UPDATE_RECORDING_GROUP)
  @actionCreator(REDUX_NAMESPACE)
  setScaleOfMultipleShots(
    scaleX,
    scaleY,
    transformElementIds,
    currentFrame = DEFAULT_KEYFRAME_NUMBER,
    isUserTransform = true,
  ) {
    return {
      currentFrame,
      isUserTransform,
      scaleX,
      scaleY,
      transformElementIds,
    };
  },

  @recordForUndo(SCALE_UPDATE_RECORDING_GROUP)
  @actionCreator(REDUX_NAMESPACE)
  setScaleOfMultipleShotsAndReposition(
    transformElementIds,
    scaleX,
    scaleY,
    layers,
    bounds,
    xValues,
    yValues,
    currentFrame = DEFAULT_KEYFRAME_NUMBER,
    isUserTransform = true,
  ) {
    return {
      bounds,
      currentFrame,
      isUserTransform,
      layers,
      scaleX,
      scaleY,
      transformElementIds,
      xValues,
      yValues,
    };
  },

  @actionCreator(REDUX_NAMESPACE)
  setScaleOfMultipleShotsAndRepositionOnPaste(
    transformElementIds,
    scaleX,
    scaleY,
    layers,
    bounds,
    xValues,
    yValues,
    currentFrame = DEFAULT_KEYFRAME_NUMBER,
    isUserTransform = true,
  ) {
    return {
      bounds,
      currentFrame,
      isUserTransform,
      layers,
      scaleX,
      scaleY,
      transformElementIds,
      xValues,
      yValues,
    };
  },

  @actionCreator(REDUX_NAMESPACE)
  setActiveShotElementId(activeShotElementId) {
    return {
      activeShotElementId,
    };
  },

  @enqueueGAEvent(
    EVENT_CATEGORY.UI.NAME,
    EVENT_CATEGORY.UI.ACTION.LEFT_PANEL,
    EVENT_CATEGORY.UI.EVENT_LABEL.LEFT_PANEL.TAB_SELECTION,
  )
  @actionCreator(REDUX_NAMESPACE)
  setActiveLeftPanelTab(activeLeftPanelTab) {
    return {
      activeLeftPanelTab,
    };
  },
};

export default ACTION_CREATORS;
